import React, { lazy, useContext, useEffect } from "react";
import { BrowserRouter as Router, Navigate, useRoutes } from "react-router-dom";
import { DndProvider } from "react-dnd";
import { MultiBackend } from "react-dnd-multi-backend";
import { HTML5toTouch } from "rdndmb-html5-to-touch"; // or any other pipeline

import { AuthProvider, AuthContext } from "./context/AuthContext";
import TranslationProvider from "./context/Internationalization";
import { SnackBarProvider } from "./context/SnackBarContext";
import InvitationProvider from "./context/InvitationContext";
import SuperAdminProvider from "./context/SuperAdminContext";
import { GoogleMapsProvider } from "./context/GoogleMapsContext";
import { AppProvider } from "./context/AppContext";
import {
  BillingGateProvider,
  useBillingGate,
} from "./context/BillingGateContext";
import { isClubAdmin, isSuperAdmin } from "./utility/Utils";
import { checkAppVersion } from "./auth/utils";

import AppShell from "./AppShell";
import Club from "./pages/Club/Club";
import Login from "./pages/Login/Login";
import SuperAdminClubs from "./pages/SuperAdmin/SuperAdminClubs";
import SuperAdminImpersonate from "./pages/SuperAdmin/SuperAdminImpersonate";
import SuperAdminRoles from "./pages/SuperAdmin/SuperAdminRoles";

const Home = lazy(() => import("./pages/Home/Home"));
const Files = lazy(() => import("./pages/Files/Files"));
const Shop = lazy(() => import("./pages/Shop/Shop"));
const Billing = lazy(() => import("./pages/Billing/Billing"));
const Forms = lazy(() => import("./pages/Forms/Forms"));

function BillingProtectedRoute({ user, children }) {
  const { state } = useBillingGate();

  if (isSuperAdmin(user)) return children;

  if (state.loading || !state.checked) return null; // or spinner

  if (!state.accessAllowed) {
    return <Navigate to="/billing" replace />;
  }

  return children;
}

function getLoginPathWithMagicLinkParams() {
  const searchParams = new URLSearchParams(window.location.search);
  const isMagicLink =
    searchParams.get("mode") === "signIn" && searchParams.get("oobCode");

  return isMagicLink ? `/login${window.location.search}` : "/login";
}

function App() {
  const auth = useContext(AuthContext);

  const user = auth.authState.user;

  const isUserSuperAdmin = isSuperAdmin(user);
  const isUserClubAdmin = isClubAdmin(user);

  let routes = [
    {
      path: "/login",
      element: <Login />,
    },
    {
      path: "/",
      element:
        auth.isAuthenticated() && (isUserClubAdmin || isUserSuperAdmin) ? (
          <BillingProtectedRoute user={user}>
            <Navigate to="/activities" />
          </BillingProtectedRoute>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: "/*",
      element:
        auth.isAuthenticated() && (isUserClubAdmin || isUserSuperAdmin) ? (
          <BillingProtectedRoute user={user}>
            <AppShell>
              <Home />
            </AppShell>
          </BillingProtectedRoute>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: "/club/*",
      element:
        auth.isAuthenticated() && (isUserClubAdmin || isUserSuperAdmin) ? (
          <BillingProtectedRoute user={user}>
            <AppShell>
              <Club />
            </AppShell>
          </BillingProtectedRoute>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: "/files/*",
      element:
        auth.isAuthenticated() && (isUserClubAdmin || isUserSuperAdmin) ? (
          <BillingProtectedRoute user={user}>
            <AppShell>
              <Files />
            </AppShell>
          </BillingProtectedRoute>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: "/shop/*",
      element:
        auth.isAuthenticated() && (isUserClubAdmin || isUserSuperAdmin) ? (
          <BillingProtectedRoute user={user}>
            <AppShell>
              <Shop />
            </AppShell>
          </BillingProtectedRoute>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: "/forms/*",
      element:
        auth.isAuthenticated() && (isUserClubAdmin || isUserSuperAdmin) ? (
          <BillingProtectedRoute user={user}>
            <AppShell>
              <Forms />
            </AppShell>
          </BillingProtectedRoute>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: "/billing/*",
      element:
        auth.isAuthenticated() && (isUserClubAdmin || isUserSuperAdmin) ? (
          <AppShell>
            <Billing />
          </AppShell>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: "/super-admin/clubs",
      element:
        auth.isAuthenticated() && isUserSuperAdmin ? (
          <AppShell>
            <SuperAdminClubs />
          </AppShell>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: "/super-admin/impersonate",
      element:
        auth.isAuthenticated() && isUserSuperAdmin ? (
          <AppShell>
            <SuperAdminImpersonate />
          </AppShell>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: "/super-admin/roles",
      element:
        auth.isAuthenticated() && isUserSuperAdmin ? (
          <AppShell>
            <SuperAdminRoles />
          </AppShell>
        ) : (
          <Navigate to={getLoginPathWithMagicLinkParams()} />
        ),
    },
    {
      path: "*",
      element: <Navigate to="/" />,
    },
  ];
  if (!isUserSuperAdmin) {
    routes = routes
      .filter((route) => route.path !== "/super-admin/clubs")
      .filter((route) => route.path !== "/super-admin/impersonate")
      .filter((route) => route.path !== "/super-admin/roles");
  }

  return (
    <SuperAdminProvider isUserSuperAdmin={isUserSuperAdmin}>
      {useRoutes(routes)}
    </SuperAdminProvider>
  );
}

const AppWrapper = () => {
  useEffect(() => {
    checkAppVersion();
  }, []);

  return (
    <div>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <div className="Container">
          <AuthProvider>
            <BillingGateProvider>
              <AppProvider>
                <TranslationProvider>
                  <SnackBarProvider>
                    <InvitationProvider>
                      <DndProvider
                        backend={MultiBackend}
                        options={HTML5toTouch}
                      >
                        <GoogleMapsProvider>
                          <App />
                        </GoogleMapsProvider>
                      </DndProvider>
                    </InvitationProvider>
                  </SnackBarProvider>
                </TranslationProvider>
              </AppProvider>
            </BillingGateProvider>
          </AuthProvider>
        </div>
      </Router>
    </div>
  );
};

export default AppWrapper;
